import React, { useState } from 'react';
import { Alert, Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { Button, Table, Title } from 'ui';
import { Switch } from 'components/Form';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import { ICONS } from 'config/icons';
import FlexRow from 'components/FlexRow';
import { UserValidationRule, UserValidationType } from '../../types';
import ValidationRuleFormModal, { ValidationRuleModalItem } from './ValidationRuleFormModal';

interface GlobalValidationRulesProps {
    validationType: UserValidationType;
    isPlatformAdmin: boolean;
}

// GLOBAL tab of the user_validations screen (CSV-1957). RESERVED and NON_RESERVABLE both live in
// the GLOBAL tab only, editable exclusively by SUPER_ADMIN / DISTRIBUTOR_ADMIN.
const GlobalValidationRules: React.FC<GlobalValidationRulesProps> = ({ validationType, isPlatformAdmin }) => {
    const t = useT();

    const reservedUrl = `api/v1/user-validations/${validationType}/reserved`;
    const nonReservableUrl = `api/v1/user-validations/${validationType}/non-reservable`;

    const [reservedReload, setReservedReload] = useState(false);
    const [reservedItem, setReservedItem] = useState<ValidationRuleModalItem>(null);

    const [nonReservableReload, setNonReservableReload] = useState(false);
    const [nonReservableItem, setNonReservableItem] = useState<ValidationRuleModalItem>(null);

    // Quick inline toggle for the non-reservable guard list - this is the "deactivate instead of
    // delete" mitigation from the analysis: turning a row off, not removing it, is how you free a
    // value up for reservation again.
    const { appendData: toggleNonReservableActive } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: `${nonReservableUrl}/:id`,
            method: 'PUT',
        },
        onSuccess: () => setNonReservableReload(true),
    });

    const reservedColumns: ColumnsType<UserValidationRule> = [
        { title: t('system.environment.user_validation_value'), dataIndex: 'value', key: 'value' },
        {
            title: t('system.environment.user_validation_owners'),
            key: 'owners',
            render: (_text: unknown, record: UserValidationRule) => (
                <>
                    {(record.owners ?? []).map((owner) => (
                        <Tag key={`${owner.ownerType}-${owner.ownerId}`}>
                            {owner.ownerName ?? `${owner.ownerType} #${owner.ownerId}`}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: t('general.status'),
            key: 'isActive',
            render: (_text: unknown, record: UserValidationRule) => (
                <Switch isFormItem={false} disabled checked={record.isActive} />
            ),
        },
        { title: t('general.description'), dataIndex: 'description', key: 'description' },
        ...(isPlatformAdmin
            ? [
                  {
                      title: t('general.actions'),
                      key: 'actions',
                      render: (_text: unknown, record: UserValidationRule) => (
                          <a onClick={() => setReservedItem(record)}>
                              <i className={ICONS.EDIT} />
                          </a>
                      ),
                  },
              ]
            : []),
    ];

    const nonReservableColumns: ColumnsType<UserValidationRule> = [
        { title: t('system.environment.user_validation_value'), dataIndex: 'value', key: 'value' },
        {
            title: t('general.status'),
            key: 'isActive',
            render: (_text: unknown, record: UserValidationRule) =>
                isPlatformAdmin ? (
                    <Switch
                        isFormItem={false}
                        checked={record.isActive}
                        onChange={(checked: boolean) =>
                            toggleNonReservableActive(
                                {
                                    ruleType: 'NON_RESERVABLE',
                                    value: record.value,
                                    description: record.description,
                                    isActive: checked,
                                },
                                { id: record.id }
                            )
                        }
                    />
                ) : (
                    <Switch isFormItem={false} disabled checked={record.isActive} />
                ),
        },
        { title: t('general.description'), dataIndex: 'description', key: 'description' },
        ...(isPlatformAdmin
            ? [
                  {
                      title: t('general.actions'),
                      key: 'actions',
                      render: (_text: unknown, record: UserValidationRule) => (
                          <a onClick={() => setNonReservableItem(record)}>
                              <i className={ICONS.EDIT} />
                          </a>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <>
            <FlexRow
                left={<Title title="system.environment.user_validation_reserved" />}
                right={
                    isPlatformAdmin ? (
                        <Button
                            type="primary"
                            onClick={() => setReservedItem({ id: 'create' })}
                            message="system.environment.user_validation_add"
                            icon={ICONS.ADD}
                        />
                    ) : undefined
                }
            />
            <Table
                columns={reservedColumns}
                url={reservedUrl}
                setReload={setReservedReload}
                reload={reservedReload}
                rowSelection={false}
                rowKey="id"
            />

            <FlexRow
                left={<Title title="system.environment.user_validation_non_reservable" className="mt-4" />}
                right={
                    isPlatformAdmin ? (
                        <Button
                            type="primary"
                            onClick={() => setNonReservableItem({ id: 'create' })}
                            message="system.environment.user_validation_add"
                            icon={ICONS.ADD}
                        />
                    ) : undefined
                }
            />
            <Alert
                className="mb-3"
                message={t('system.environment.user_validation_non_reservable_hint')}
                type="info"
                showIcon
            />
            <Table
                columns={nonReservableColumns}
                url={nonReservableUrl}
                setReload={setNonReservableReload}
                reload={nonReservableReload}
                rowSelection={false}
                rowKey="id"
            />

            <ValidationRuleFormModal
                item={reservedItem}
                baseUrl={reservedUrl}
                fixedRuleType="RESERVED"
                showOwners
                onCancel={() => setReservedItem(null)}
                onSuccess={() => {
                    setReservedItem(null);
                    setReservedReload(true);
                }}
            />
            <ValidationRuleFormModal
                item={nonReservableItem}
                baseUrl={nonReservableUrl}
                fixedRuleType="NON_RESERVABLE"
                onCancel={() => setNonReservableItem(null)}
                onSuccess={() => {
                    setNonReservableItem(null);
                    setNonReservableReload(true);
                }}
            />
        </>
    );
};

export default GlobalValidationRules;
